import React, { useState, useEffect } from 'react';
import '../../styles/components/TemplatesList.scss'
import { createAppTemplate, updateAppTemplate, deleteAppTemplateAll, getAppTemplateDomainById } from '../../api/appTemplatesApi.js';
import { getAppKeywords } from '../../api/appKeyWordsApi.js';
import { getAppUserAllById } from '../../api/appWorkplacesUsersApi.js';
import { useLoader } from '../../context/LoaderContext.js';
import { Box, Grid, Card, CardContent, Typography, Button, Fab } from '@mui/material';
import { Snackbar, Alert } from '@mui/material';
import {
  Cake as CakeIcon,
  WorkHistory as WorkHistoryIcon,
  Group as GroupIcon,
} from '@mui/icons-material';
import { useDomain } from '../../context/DomainContext.js';
import TemplateModal from './TemplateModal.js';
import DeleteModal from '../DeleteModal.js';
import TemplatesDataGrid from './TemplateDataGrid.js';
import { useAuth } from '../../context/AuthContext.js';

function SpecialTemplatesList() {
  const [templates, setTemplates] = useState([]);
  const [keywords, setKeywords] = useState([]);
  const [users, setUsers] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateTypeToCreate, setTemplateTypeToCreate] = useState('birthday');
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState({ open: false, text: '', error: false });
  const { showLoader, hideLoader } = useLoader();
  const { selectedDomain } = useDomain();
  const { user } = useAuth();

  const loadTemplates = () => {
    showLoader();
    getAppTemplateDomainById(selectedDomain)
      .then(response => {
        // Filtrar solo templates especiales (birthday y anniversary)
        const specialTemplates = response.data.filter(
          t => t.template_type === 'birthday' || t.template_type === 'anniversary'
        );
        setTemplates(specialTemplates);
      })
      .finally(() => { hideLoader() })
      .catch(error => console.error('Error fetching templates:', error));
  };

  const loadUsers = () => {
    showLoader();
    getAppUserAllById(selectedDomain)
      .then(response => {
        setUsers(response.data);
      })
      .finally(() => { hideLoader() })
      .catch(error => console.error('Error fetching users:', error));
  };

  const loadKeywords = () => {
    showLoader();
    getAppKeywords()
      .then(response => {
        setKeywords(response.data)
      })
      .finally((data) => { hideLoader() })
      .catch(error => console.error('Error fetching keywords:', error));
  };

  const handleOpenDeleteModal = (template) => {
    setSelectedTemplate(template);
    setOpenDeleteModal(true);
  };

  const handleCloseConfirmModal = () => {
    setOpenDeleteModal(false);
    setSelectedTemplate(null);
  };

  const handleOpenModal = (template, templateType = 'birthday') => {
    setTemplateTypeToCreate(templateType);
    setSelectedTemplate(template);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedTemplate(null);
    setTemplateTypeToCreate('birthday');
  };

  const handleSaveTemplate = async (data, isNew) => {
    try {
      showLoader();
      let templateMessage = isNew ? 'La plantilla se creó correctamente' : 'La plantilla se guardó correctamente';
      isNew ? await createAppTemplate(data) : await updateAppTemplate(selectedTemplate.id, data);

      setOpenModal(false);
      setSelectedTemplate(null);
      setTemplateTypeToCreate('birthday');
      setOpenSnackbar({ open: true, text: templateMessage, error: false });
      loadTemplates();
    } catch (error) {
      if (error.type === 'duplicate') {
        setOpenSnackbar({ open: true, text: `Error: ${error.message}`, error: true });
      } else {
        setOpenSnackbar({ open: true, text: `Error: ${error.message}`, error: true });
      }
    } finally {
      hideLoader();
    }
  };

  const handleDeleteTemplate = async (id) => {
    try {
      showLoader();
      await deleteAppTemplateAll(id);
      setOpenDeleteModal(false);
      setSelectedTemplate(null);
      setOpenSnackbar({ open: true, text: 'La plantilla se eliminó correctamente', error: true });
      loadTemplates();
    } catch (error) {
      setOpenSnackbar({ open: true, text: 'Hubo un error al eliminar la plantilla', error: true });
    } finally {
      hideLoader();
    }
  };

  const handleClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setOpenSnackbar({ open: false, text: '', error: false });
  };

  // Contar templates por tipo
  const birthdayCount = templates.filter(t => t.template_type === 'birthday').length;
  const anniversaryCount = templates.filter(t => t.template_type === 'anniversary').length;

  // Contar usuarios activos
  const activeUsersCount = Array.isArray(users) ? users.filter(u => u.is_active).length : 0;

  useEffect(() => {
    if (selectedDomain) {
      loadTemplates();
      loadKeywords();
      loadUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDomain]);

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      maxHeight: 'calc(100vh - 234px)',
      pt: 3,
      pb: 1
    }}>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent className='resume-container'>
              <Fab color="secondary" aria-label="add" className='resume-icon'>
                <CakeIcon />
              </Fab>
              <Typography variant="subtitle1" color="textSecondary" gutterBottom className='resume-title'>
                Plantillas Cumpleaños
              </Typography>
              <Typography variant="h5" component="div" className='resume-value'>
                {birthdayCount}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent className='resume-container'>
              <Fab color="info" aria-label="add" className='resume-icon'>
                <WorkHistoryIcon />
              </Fab>
              <Typography variant="subtitle1" color="textSecondary" gutterBottom className='resume-title'>
                Plantillas Aniversario
              </Typography>
              <Typography variant="h5" component="div" className='resume-value'>
                {anniversaryCount}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Botones para crear plantillas y contadores de usuarios */}
      {user?.isAdmin && (
        <Box sx={{
          display: 'flex',
          gap: 2,
          mb: 3,
          flexWrap: 'wrap',
          alignItems: 'center'
        }}>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<CakeIcon />}
            onClick={() => handleOpenModal({}, 'birthday')}
          >
            Plantilla Cumpleaños
          </Button>

          <Button
            variant="contained"
            color="info"
            startIcon={<WorkHistoryIcon />}
            onClick={() => handleOpenModal({}, 'anniversary')}
          >
            Plantilla Aniversario
          </Button>

          {/* Contadores de usuarios (compactos) */}
          <Box sx={{
            display: 'flex',
            gap: 2,
            ml: 'auto',
            alignItems: 'center'
          }}>
            <Card sx={{ minWidth: 150 }}>
              <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <GroupIcon color="primary" />
                  <Box>
                    <Typography variant="caption" color="textSecondary" display="block">
                      Total Usuarios
                    </Typography>
                    <Typography variant="h6">
                      {users.length}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>

            <Card sx={{ minWidth: 150 }}>
              <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <GroupIcon color="success" />
                  <Box>
                    <Typography variant="caption" color="textSecondary" display="block">
                      Usuarios Activos
                    </Typography>
                    <Typography variant="h6">
                      {activeUsersCount}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Box>
      )}

      <Card className='template-list-datagrid-card' >
        <CardContent>
          <TemplatesDataGrid
            templates={templates}
            handleOpenDeleteModal={handleOpenDeleteModal}
            handleOpenModal={handleOpenModal}
          />
        </CardContent>
      </Card>

      <DeleteModal
        openConfirmModal={openDeleteModal}
        selectedItem={selectedTemplate}
        handleCloseConfirmModal={handleCloseConfirmModal}
        onDelete={handleDeleteTemplate}
        message={'¿Está seguro de eliminar la plantilla: ' + selectedTemplate?.template_name + '?'}
      />

      <TemplateModal
        openModal={openModal}
        selectedTemplate={selectedTemplate}
        templateTypeToCreate={templateTypeToCreate}
        handleCloseModal={handleCloseModal}
        handleSaveTemplate={handleSaveTemplate}
        keywords={keywords}
      />
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        autoHideDuration={6000}
        open={openSnackbar.open}
        onClose={handleClose}
      >
        <Alert
          onClose={handleClose}
          severity={openSnackbar.error ? 'error' : 'success'}
          variant="filled"
        >
          {openSnackbar.text}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default SpecialTemplatesList;